/**
 * Kiosk Portal Store Configuration
 *
 * Minimal store for kiosk portal (auth only for now)
 */

import {
  configureStore,
  combineReducers,
  createSagaMiddleware,
  all,
  fork,
  configureLocaleProvider,
  configureTenantIdProvider,
} from '@ufc/core'

// Auth domain
import { authReducer, authSaga } from '@ufc/domains/auth'

// Portal init (calls /api/users/me and distributes admin context, feature flags, etc.)
import { initReducer, initSaga } from '@ufc/domains/portal-init'

// Admin context (optional - for tenant switching if needed)
import { adminContextReducer, adminContextActions, selectTenantContext } from '@ufc/domains/admin-context'

// Shipments domain (Scan feature)
import { scanReducer, scanSaga, weighbridgeReducer, weighbridgeSaga } from '@ufc/domains/shipments'
// Support domain
import { supportReducer, supportSaga } from '@ufc/domains/support'

// Queue domain (Join Queue feature)
import { queueReducer, queueSaga } from '@ufc/domains/queue'
import { tripPlanningLiveReducer, tripPlanningLiveSaga } from '@ufc/domains/trip-planning'
import { citiesReducer, citiesSaga } from '@ufc/domains/cities'
import { getStoredLanguage } from '@/lib/language-preferences'
import { kioskConfigReducer, kioskConfigSaga } from './kiosk-config.slice'
import { authMonitoringSaga } from './auth-monitoring.sagas'

// Root reducer (minimal for kiosk)
const rootReducer = combineReducers({
  auth: authReducer,
  init: initReducer,
  // Admin context for tenant switching (if needed)
  adminContext: adminContextReducer,
  // Scan feature state
  scan: scanReducer,
  // Weighbridge feature state
  weighbridge: weighbridgeReducer,
  // Queue management
  queue: queueReducer,
  // City selection for orders missing city
  cities: citiesReducer,
  // Support
  support: supportReducer,
  // Kiosk queue config
  kioskConfig: kioskConfigReducer,
  tripPlanningLive: tripPlanningLiveReducer,
})

// Root saga
function* rootSaga() {
  yield all([
    fork(authSaga),
    fork(initSaga),
    fork(scanSaga),
    fork(weighbridgeSaga),
    fork(queueSaga),
    fork(citiesSaga),
    fork(supportSaga),
    fork(kioskConfigSaga),
    fork(authMonitoringSaga),
    fork(tripPlanningLiveSaga),
  ])
}

// Create saga middleware
const sagaMiddleware = createSagaMiddleware()

// Create store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false, // We use sagas instead
    }).concat(sagaMiddleware),
  devTools: import.meta.env.DEV,
})

// Run the root saga
sagaMiddleware.run(rootSaga)

// Restore admin context tenant selection from localStorage (if admin-context is used)
store.dispatch(adminContextActions.restoreFromStorage())

// Configure HTTP client to include X-Tenant-Id header when admin has selected a tenant
// This ensures all API calls are scoped to the selected tenant
configureTenantIdProvider({
  getTenantId: () => selectTenantContext(store.getState()),
})

configureLocaleProvider({
  getLocale: () => getStoredLanguage(),
})

// Export types
export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
